<template>
    <div v-if="internalOrder" class="receipt-correct">
        <v-alert
            class="ma-4"
            border="left"
            prominent
            color="warning"
            type="info"
            text
        >
            {{ $t('order.correct_message') }}
        </v-alert>

        <v-data-table
            ref="table"
            class="items-table"
            :headers="headers"
            :items="internalOrder.items"
            :items-per-page="-1"
            hide-default-footer
            :mobile-breakpoint="0"
            dense
        >
            <template v-slot:item.selected="{ item }">
                <v-checkbox
                    v-show="item.updated"
                    :value="!!item.updated"
                    color="error"
                    dense
                    hide-details
                    @change="unselectItem(item)"
                />
            </template>

            <template v-slot:item.code="{ item }">
                <div class="code" :class="{ updated: item.updated }">
                    {{ item.product ? item.product.code : '' }}
                </div>
            </template>

            <template v-slot:item.name="{ item }">
                <div class="name" :class="{ updated: item.updated }">
                    {{ item.name }}
                    <span v-if="item.getTaxRateLabel()">({{ item.getTaxRateLabel() }})</span>
                </div>
            </template>

            <template v-slot:item.unitPrice="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.getUnitPriceAfterDiscount().toFormat('0.00') }}
                </div>
            </template>

            <template v-slot:item.count="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.getCount() }}
                </div>
            </template>

            <template v-slot:item.total="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.getToPay().toFormat('0.00') }}
                </div>
            </template>

            <template v-slot:item.additionalCount="{ item }">
                <div v-if="item.updated" class="wfull updated">
                    {{ item.getAdditionalCount() }}
                </div>
                <v-edit-dialog
                    v-if="!item.updated"
                    large
                    :cancel-text="$t('general.cancel')"
                    :save-text="$t('general.save')"
                    @open="openEditDialog(item)"
                    @save="editItem(item)"
                >
                    <div class="wfull">
                        <v-icon size="16" class="mr-1">fal fa-pen</v-icon>
                        {{ item.getAdditionalCount() }}
                    </div>
                    <template v-slot:input>
                        <div class="mt-4 text-h6">
                            {{ $t('general.refund_count') }}
                        </div>
                        <number-field
                            ref="count"
                            v-model="count"
                            :label="$t('general.quantity')"
                            :precision="3"
                            single-line
                            unsigned
                        />
                    </template>
                </v-edit-dialog>
            </template>

            <template v-slot:item.refund="{ item }">
                <div :class="{ updated: item.updated }">
                    {{ item.getAdditionalToPay().toFormat('0.00') }}
                </div>
            </template>
        </v-data-table>

        <div class="summary">
            <div class="taxes-container">
                <template v-for="(tax, index) in correctedOrder.taxes">
                    <div
                        v-if="!tax.id && tax.price_before_tax"
                        :key="index"
                        class="summary-item"
                    >
                        <div>{{ $t('order.exempt_from_tax') }} {{ tax.label.replace('VAT_','') }}:</div>
                        <div class="amount">{{ Money.parse(tax.price_before_tax).abs(locale === 'sr').toFormat('0.00') }}</div>
                    </div>
                </template>

                <div v-if="correctedOrder.taxes.some(tax => !tax.id)" class="item-divider"></div>

                <template v-for="(tax, index) in correctedOrder.taxes">
                    <div
                        v-if="tax.id && tax.price_before_tax"
                        :key="'beforetax-' + index"
                        class="summary-item"
                    >
                        <div>
                            <span v-if="tax.name">{{ tax.name }} - </span>
                            <span>{{ `${tax.label} ${$t('general.price_pre_tax').toLowerCase()}:` }}</span>
                        </div>
                        <div class="amount">{{ Money.parse(tax.price_before_tax).abs(locale === 'sr').toFormat('0.00') }}</div>
                    </div>

                    <div
                        v-if="tax.id"
                        :key="index"
                        class="summary-item"
                    >
                        <div>
                            <span v-if="tax.name">{{ tax.name }} - </span>
                            <span>{{ `${tax.label} (${tax.rate}%):` }}</span>
                        </div>
                        <div class="amount">{{ Money.parse(tax.value).abs(locale === 'sr').toFormat('0.00') }}</div>
                    </div>

                    <div v-if="tax.id" class="item-divider"></div>
                </template>

                <div v-if="correctedOrder.getExemptFromTax().toUnit() != 0" class="summary-item">
                    <div>{{ $t('invoice_preview.summary.exempt_from_tax') }}:</div>
                    <div class="amount">{{ correctedOrder.getExemptFromTax().abs(locale === 'sr').toFormat('0.00') }}</div>
                </div>

                <div class="summary-item">
                    <div>{{ $t('invoice_preview.summary.price_before_tax') }}:</div>
                    <div class="amount">{{ correctedOrder.getPriceBeforeTax().abs(locale === 'sr').toFormat('0.00') }}</div>
                </div>

                <div class="summary-item">
                    <div>{{ $t('invoice_preview.summary.tax') }}:</div>
                    <div class="amount">{{ correctedOrder.getTax().abs(locale === 'sr').toFormat('0.00') }}</div>
                </div>

                <div class="item-divider"></div>

                <div class="summary-item to-pay mt-2">
                    <div>{{ $t('general.refund') }}:</div>
                    <div class="amount">{{ correctedOrder.getToPay().abs(locale === 'sr').toFormat('0.00') }}</div>
                </div>
            </div>
        </div>
    </div>

</template>

<script>
import NumberField from '@/components/NumberField'

export default {
    components: {
        NumberField
    },

    props: {
        order: {
            type: Object,
            default: () => null
        },
    },

    created() {
        this.internalOrder = this.order ? this.order.$duplicate() : null;
        this.correctedOrder = this.createCorrectedOrder();
    },

    data() {
        return {
            count: null,
            internalOrder: null,
            correctedOrder: null
        };
    },

    watch: {
        order(val) {
            this.internalOrder = val ? val.$duplicate() : null;
            this.correctedOrder = this.createCorrectedOrder();
        }
    },

    computed: {
        headers() {
            const headers = [{ text: '', value: 'selected', sortable: false, class: 'pa-0' }];

            headers.push({ text: this.$t('general.code'), value: 'code' });
            headers.push({ text: this.$tc('general.product', 1), value: 'name' });
            headers.push({ text: this.$t('general.price'), value: 'unitPrice' });
            headers.push({ text: this.$t('general.quantity'), value: 'count' });
            headers.push({ text: this.$t('general.total'), value: 'total' });
            headers.push({ text: this.$t('general.refund_count'), value: 'additionalCount', class: 'separator', cellClass: 'separator' });
            headers.push({ text: this.$t('general.refund'), value: 'refund' });

            return headers
        },
    },

    methods: {
        createCorrectedOrder() {
            const updatedItems = this.internalOrder.items
                .filter(item => !!item.updated)
                .map(item => {
                    const itemCopy = item.$duplicate();
                    itemCopy.count = itemCopy.additional_count;
                    itemCopy.additional_count = 0;

                    delete itemCopy.updated
                    return itemCopy
                })

            const curShift = Shift.current();

            const correctedOrder = new Order({
                shift_id: curShift ? curShift.id : null,
                shift_hash: curShift ? curShift.hash : null,
                items: updatedItems,
            })

            correctedOrder.taxes = Order.calculateTaxes(correctedOrder);
            this.$emit('enable-issue', updatedItems.length > 0);

            return correctedOrder
        },

        openEditDialog(item) {
            this.count = -1 * item.getAdditionalCount();

            this.$nextTick(() => {
                if(this.$refs.count && this.$refs.count.$refs.textField) {
                    this.$refs.count.$refs.textField.$refs.input.setSelectionRange(0, 10000)
                }
            })
        },

        editItem(item) {
            const count = _.round(-1 * this.count, 3)
            const resultingCount = item.getCount() + count
            if(count == 0 || resultingCount < 0) return;

            item.updateAdditionalCount(count);
            item.updated = true;
            this.correctedOrder = this.createCorrectedOrder();
        },

        unselectItem(item) {
            item.updated = false;
            item.updateAdditionalCount(0);
            this.correctedOrder = this.createCorrectedOrder();
        },

        correctOrder() {
            this.$emit('create', { items: this.correctedOrder.items })
        }
    }
};
</script>

<style lang="scss" scoped>
.receipt-correct {
    .internal-number {
        max-width: 300px;
    }

    ::v-deep .items-table {
        th {
            padding: 5px;
            vertical-align: middle;
            background: #f2f2f2;
        }

        td {
            font-size: 13px !important;
            padding: 5px;

            &:first-child {
                padding: 0;
                width: 42px;
                text-align: right !important;

                .v-input {
                    width: 30px;
                    margin-left: 12px;
                    margin-top: 0px;
                    padding: 0px;

                    .v-icon {
                        color: var(--v-error-base);
                    }
                }
            }

            .code {
                max-width: 60px;
            }

            .name {
                min-width: 120px;
            }

            .updated {
                color: var(--v-error-base);
            }

            .v-small-dialog__activator__content {
                width: 100%;
            }
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }
    }

    .summary {
        font-size: 14px;
        margin-top: 12px;
        display: flex;
        justify-content: flex-end;
        padding: 12px;

        .item-divider {
            border-bottom: 1px solid black;
            margin: 5px 0;
        }

        .info-container {
            flex: 1;
            padding: 12px;
        }

        .taxes-container {
            padding: 12px;
            min-width: 300px;
        }

        .summary-item {
            padding: 3px;
            display: flex;
            justify-content: space-between;

            &.total {
                font-weight: 600;
                font-size: 16px;
            }

            &.to-pay {
                font-weight: 600;
                font-size: 16px;
            }

            .amount {
                text-align: right;
            }
        }
    }
}
</style>
